import _ from 'lodash'
import {
    // Constants
    SET_PROGRESS,
    SET_PROCESS_RUNNING,
    SET_SERVERS,
    SET_SETTINGS,
    SET_DOWNLOAD_EVERYTHING,
    SET_RUN_CLIENTS_WITH_LOGGING,
    SET_RUN_CLIENTS_WITH_DX9,
    SET_SHOULD_MINIMIZE_TO_TRAY,
    SET_LOCALE,
    SET_TEST_SERVERS,
    SET_SERVER_PROFILES,
    SET_ACTIVE_SERVER,

    // actions
    setActiveServer,
    localeChanged,
    settingsChanged,
    serverProfilesChanged,
    serverListChanged,
    setReady,
    runningProcessesChanged,
    setProcessManagerErrorText,
    activeServerChanged,
    setPlayable,

    // selectors
    getProgressForServer,
    getServers,
    getBackendSettings,
    getLocale,
    getServerProfiles,
    getRunningProcesses,
    getDefaultServer,
} from '../data'
import { actions } from '../imports'
import getLocalizedTexts from '../localization'

function dispatchChanges(store, prevLocale = '', newLocale = '', fromBackend = false) {
    // Locale has changed...
    if (fromBackend || prevLocale !== newLocale) {
        console.info(`locale switched from ${prevLocale} to ${newLocale}`)
        store.dispatch(localeChanged(prevLocale, newLocale))
        const text = getLocalizedTexts(newLocale)
        store.dispatch(setProcessManagerErrorText(text.process_manager_error_client_closed))
        // The backend has now been initialized with a language and is therefore ready for display
        store.dispatch(setReady(true))
    }

    if (!fromBackend) {
        const settings = getBackendSettings(store.getState())
        store.dispatch(settingsChanged(settings))
    }
}

const BackendMiddleware = store => next => (action) => {
    switch (action.type) {
        // Whenever we set the progress, we want to log it as well,
        // but only if the value has changed
        case SET_PROGRESS: {
            const serverId = action.payload.serverId
            const prevState = getProgressForServer(store.getState(), serverId)
            const result = next(action)
            const newState = getProgressForServer(store.getState(), serverId)

            // If the value has changed then we update
            // Otherwise we are always updating on 'Checking hosts'
            if (prevState.value !== newState.value) {
                // Create the message used for update progress logs
                const message = `updateProgress request for ${serverId} ${
                    newState.value === 1 ? 'completed' : `(${(newState.value * 100).toFixed(2)}%)`
                }`

                // Log the message if on development environment or if
                // the change is significant enough
                if (process.env.NODE_ENV === 'development') {
                    store.dispatch(actions.log(message))
                } else if (Math.floor(prevState.value * 100) < Math.floor(newState.value * 100)) {
                    store.dispatch(actions.log(message))
                }

                if (newState.value < 1) {
                    // TODO: 2018-08-22 baldvinth - Remove this when the backend has been fixed
                    // (todo-list-setPlayable)
                    store.dispatch(setPlayable(serverId, false))
                }

                if (newState.value === 1) {
                    // TODO: 2018-08-22 baldvinth - Remove this when the backend has been fixed
                    // (todo-list-setPlayable)
                    store.dispatch(setPlayable(serverId, true))
                }
            }
            return result
        }

        // When we receive a list of servers to use,
        // We make sure to update the active server as well
        case SET_SERVERS: {
            // Complete the action
            const result = next(action)

            let activeServerId

            // First we get the server list from state
            const servers = getServers(store.getState())

            // We also need to let everyone know that the serverlist has changed
            // We do this to force the sync reload
            store.dispatch(serverListChanged(servers))

            // Now we get the hash from the location
            // We do this since if we are on a test server
            // the location includes the server shortname after the #
            // Fx: .../release/2695/?running#server:sisi
            const hash = window.location.hash

            // We found a hash, so we are on a test server
            if (hash !== '') {
                // Find matches for hash that's passed along
                const serverHash = hash.match(/(?:server[:])([\w]+)/i)

                // If we find match for a server we update the active server
                if (
                    serverHash &&
                    serverHash[1] !== undefined &&
                    servers[serverHash[1]] !== undefined
                ) {
                    // Get the server shortname
                    activeServerId = servers[serverHash[1]].serverId
                }

                // We did not find a hash, and default server is undefined, but we still
                // have at least one server in our list
            } else if (
                servers[getDefaultServer(getLocale(store.getState()))] === undefined &&
                _.size(servers) > 0
            ) {
                // Since we have no idea what servers are in the list,
                // and since the list is an object and therefore unordered
                // we simply pick a random server from the list and set
                // as our active server
                activeServerId = _.sample(servers).serverId

                // Default server is in our list and we found no hash, we select the default server
            } else if (servers[getDefaultServer(getLocale(store.getState()))] !== undefined) {
                activeServerId = servers[getDefaultServer(getLocale(store.getState()))].serverId
            }

            // If we found a servername to use; update the state
            if (activeServerId !== undefined) {
                store.dispatch(setActiveServer(activeServerId))
            }

            return result
        }

        // We send out actions to notify
        // that language has changed, and that settings have changed
        case SET_DOWNLOAD_EVERYTHING:
        case SET_RUN_CLIENTS_WITH_LOGGING:
        case SET_RUN_CLIENTS_WITH_DX9:
        case SET_SHOULD_MINIMIZE_TO_TRAY:
        case SET_TEST_SERVERS: {
            // todo: Maybe see if the value has actually changed and only dispatch
            // if so.
            const result = next(action)
            dispatchChanges(store)
            return result
        }

        case SET_SETTINGS:
        case SET_LOCALE: {
            // First get previous language
            const prevLocale = getLocale(store.getState())
            const result = next(action)
            // Then the new language
            const newLocale = getLocale(store.getState())
            dispatchChanges(store, prevLocale, newLocale, action.type === SET_SETTINGS)
            return result
        }

        // We want to broadcast that the profile list for a certain server
        // has been updated
        case SET_SERVER_PROFILES: {
            const result = next(action)
            const profiles = getServerProfiles(store.getState(), action.payload.serverId)
            store.dispatch(serverProfilesChanged(action.payload.serverId, profiles))
            return result
        }

        // We want to broadcast that the running processes have changed
        case SET_PROCESS_RUNNING: {
            // Get the current list of running clients
            const oldList = getRunningProcesses(store.getState())
            // ex: { tq: ['ccp_rubik'], sisi: [] }

            const result = next(action)

            // Get the new list of running clients
            const newList = getRunningProcesses(store.getState())
            // ex: { tq: [], sisi: ['someuser'] }

            // Start by getting all serverIds
            const serverIds = _.union(Object.keys(oldList), Object.keys(newList))

            // Create an objects that looks like so
            // { tq: { started: [], stopped: ['ccp_rubik'] },
            //   sisi: { started: ['someuser'], stopped: [] } }
            const changes = serverIds.reduce((res, serverId) => {
                res[serverId] = {
                    started: _.difference(newList[serverId] || [], oldList[serverId] || []),
                    stopped: _.difference(oldList[serverId] || [], newList[serverId] || []),
                }
                return res
            }, {})

            store.dispatch(runningProcessesChanged(changes))

            return result
        }

        // Active server changed
        case SET_ACTIVE_SERVER: {
            const result = next(action)
            store.dispatch(activeServerChanged(action.payload))
            return result
        }

        default:
            return next(action)
    }
}

export default BackendMiddleware



// WEBPACK FOOTER //
// ./src/features/Backend/middleware/index.js